import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Zap, Locate, Camera, Plus, Save, RotateCcw, MapPin, Check,
} from "lucide-react";
import { toast } from "sonner";
import {
  PROPERTY_TYPES, ROOM_COUNTS, STATUS_LIST, CONDITION_LIST,
  FEATURES_POSITIVE, FEATURES_NEGATIVE, STATUS_TO_CATEGORY,
} from "../constants/propertyOptions";
import { compressImage } from "../utils/imageCompress";
import { saveDraft, loadDraft, clearDraft, pushPendingQuickEntry, removePendingQuickEntry, getPendingQuickEntries, newClientKey } from "../utils/autoDraft";
import MoneyInput from "./MoneyInput";
import SingleChoiceSelect from "./SingleChoiceSelect";
import {
  FLOOR_OPTIONS,
  BUILDING_AGE_OPTIONS,
  mapStoredFloorToOption,
  mapStoredAgeToOption,
  ageOptionToPayload,
  floorOptionToPayload,
} from "../constants/floorAgeOptions";
import { CURRENCIES } from "../utils/currency";
import LocationPreview from "./LocationPreview";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyQuick = {
  title: "", code: "", status: "Gezildi",
  property_type: "", room_count: "",
  current_price: "", currency: "TRY", currency_other: "",
  note: "",
  lat: null, lng: null, location_accuracy: null, location_captured_at: null,
  location: "", technical_condition: "",
  features: [],
  building_age: "",
  current_floor: null,
};

const QuickFieldEntry = ({ open, onClose, onSaved }) => {
  const [form, setForm] = useState(emptyQuick);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState([]); // files to upload after save
  const [syncState, setSyncState] = useState("idle"); // idle | saving | saved | pending | failed
  const [syncMessage, setSyncMessage] = useState("");
  const [pendingCount, setPendingCount] = useState(0);
  const [floorSelection, setFloorSelection] = useState("");
  const [ageSelection, setAgeSelection] = useState("");
  const cameraRef = useRef(null);

  // Refresh pending count whenever the dialog opens.
  useEffect(() => {
    if (open) setPendingCount(getPendingQuickEntries().length);
  }, [open]);

  // Auto-retry pending entries when the browser reports we are online.
  useEffect(() => {
    const tryFlush = async () => {
      const list = getPendingQuickEntries();
      if (list.length === 0) return;
      for (const item of list) {
        try {
          await axios.post(`${API_URL}/api/properties`, item.payload, {
            withCredentials: true,
            headers: { "x-idempotency-key": item.clientKey },
          });
          removePendingQuickEntry(item.clientKey);
        } catch (e) {
          // Leave in queue; will retry on next online event.
          break;
        }
      }
      setPendingCount(getPendingQuickEntries().length);
      onSaved?.();
    };
    window.addEventListener("online", tryFlush);
    return () => window.removeEventListener("online", tryFlush);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;
    const draft = loadDraft("quick");
    if (draft) {
      setForm({ ...emptyQuick, ...draft });
      setFloorSelection(mapStoredFloorToOption(draft.current_floor));
      setAgeSelection(mapStoredAgeToOption(draft.building_age));
      toast.info("Taslak yüklendi");
    } else {
      setForm(emptyQuick);
      setFloorSelection("");
      setAgeSelection("");
    }
    setPendingPhotos([]);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => saveDraft("quick", form), 500);
    return () => clearTimeout(t);
  }, [form, open]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleFeat = (k) => setForm(f => ({ ...f, features: f.features.includes(k) ? f.features.filter(x=>x!==k) : [...f.features, k] }));

  const captureLocation = () => {
    if (!navigator.geolocation) { toast.error("Cihaz konum desteklemiyor"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        set({
          lat: latitude, lng: longitude,
          location_accuracy: Math.round(accuracy),
          location_captured_at: new Date().toISOString(),
        });
        try {
          const { data } = await axios.get(`${API_URL}/api/reverse-geocode`, {
            params: { lat: latitude, lng: longitude }, withCredentials: true,
          });
          set({ location: data.display_name?.split(",").slice(0, 2).join(",") });
        } catch (e) { console.debug("reverse-geocode failed:", e?.message); }
        toast.success(`Konum kaydedildi (±${Math.round(accuracy)}m)`);
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        let msg = "Konum alınamadı";
        if (err.code === 1) msg = "Konum izni reddedildi. Manuel adres arama veya haritadan pin seçebilirsiniz.";
        else if (err.code === 2) msg = "Konum bilinmiyor. GPS sinyalinizi kontrol edin.";
        else if (err.code === 3) msg = "Konum alma zaman aşımına uğradı.";
        toast.error(msg, { duration: 6000 });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const addPhotos = (files) => {
    const list = Array.from(files).map((f, i) => ({ file: f, id: `p-${Date.now()}-${i}` }));
    setPendingPhotos((p) => [...p, ...list]);
  };

  const uploadPendingPhotos = async (propertyId) => {
    for (const p of pendingPhotos) {
      try {
        const compressed = await compressImage(p.file);
        const form = new FormData();
        form.append("file", compressed);
        form.append("category", "Diğer");
        form.append("is_main", pendingPhotos.indexOf(p) === 0 ? "true" : "false");
        await axios.post(
          `${API_URL}/api/properties/${propertyId}/photos`,
          form,
          { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
        );
      } catch (e) { console.error(e); }
    }
  };

  const submit = async (openNew = false) => {
    if (!form.title || form.title.trim().length === 0) {
      toast.error("Kısa başlık zorunlu");
      return;
    }
    const clientKey = newClientKey();
    setSaving(true);
    setSyncState("saving");
    setSyncMessage("Kaydediliyor…");
    try {
      const payload = { ...form };
      // Bina Yaşı — user picked an option: replace with canonical value.
      // No pick: preserve whatever was originally stored (legacy string or
      // pre-existing int). ageOptionToPayload handles int/string coercion.
      if (ageSelection !== "") {
        payload.building_age = ageOptionToPayload(ageSelection);
      } else if (typeof payload.building_age === "string" && payload.building_age.trim() === "") {
        payload.building_age = null;
      }
      // Bulunduğu Kat — same rule.
      if (floorSelection !== "") {
        payload.current_floor = floorOptionToPayload(floorSelection);
      } else if (typeof payload.current_floor === "string") {
        const t = payload.current_floor.trim();
        payload.current_floor = t === "" ? null : t.slice(0, 50);
      }
      payload.current_price = payload.current_price ? Number(payload.current_price) : null;
      payload.description = payload.note || null;
      if (payload.status) payload.category = STATUS_TO_CATEGORY[payload.status] || "Gezeceklerim";
      if (payload.current_price != null) payload.price = payload.current_price;
      payload.lat = payload.lat != null ? Number(payload.lat) : null;
      payload.lng = payload.lng != null ? Number(payload.lng) : null;

      const { data } = await axios.post(`${API_URL}/api/properties`, payload, {
        withCredentials: true,
        headers: { "x-idempotency-key": clientKey },
      });
      setSyncState("saved");
      setSyncMessage("Sunucuya kaydedildi ✓");
      toast.success("Hızlı kayıt oluşturuldu");
      clearDraft("quick");

      // Upload photos in background — don't block UI
      if (pendingPhotos.length > 0) {
        uploadPendingPhotos(data.id).then(() => {
          toast.success(`${pendingPhotos.length} fotoğraf yüklendi`);
          onSaved?.();
        });
      }

      onSaved?.();

      if (openNew) {
        setForm(emptyQuick);
        setFloorSelection("");
        setAgeSelection("");
        setPendingPhotos([]);
        setTimeout(() => { setSyncState("idle"); setSyncMessage(""); }, 1500);
      } else {
        onClose?.();
      }
    } catch (e) {
      // Persist to pending queue so the entry is NOT lost, even after
      // tab close (localStorage). The entry will be retried on next
      // window.online event OR the next successful "Kaydet" click.
      const payload = { ...form };
      payload.current_price = payload.current_price ? Number(payload.current_price) : null;
      payload.description = payload.note || null;
      if (payload.status) payload.category = STATUS_TO_CATEGORY[payload.status] || "Gezeceklerim";
      if (payload.current_price != null) payload.price = payload.current_price;

      let queued = false;
      let queueError = null;
      try {
        // Note: photos are intentionally NOT queued — File/Blob objects
        // don't reliably survive JSON round-trips in localStorage. They
        // must be re-attached after connectivity returns.
        pushPendingQuickEntry(clientKey, payload);
        queued = true;
      } catch (qerr) {
        queueError = qerr;
      }
      setPendingCount(getPendingQuickEntries().length);

      const status = e?.response?.status;
      const isOffline = !navigator.onLine || (!status && e?.message === "Network Error");

      if (!queued) {
        // NEVER show fake "Cihazda bekliyor" success if we couldn't
        // actually persist the entry — surface the real failure.
        setSyncState("failed");
        if (queueError?.message === "PAYLOAD_HAS_BINARY") {
          setSyncMessage("Senkronizasyon başarısız — payload'da desteklenmeyen alan var.");
        } else if (queueError?.name === "QuotaExceededError" || /quota/i.test(queueError?.message || "")) {
          setSyncMessage("Senkronizasyon başarısız — cihaz depolaması dolu. Bir kayıt silin ve tekrar deneyin.");
        } else {
          setSyncMessage(`Senkronizasyon başarısız — cihazda saklanamadı (${queueError?.message || "hata"}).`);
        }
        toast.error(syncMessage || "Kayıt kaybolabilir — depolama hatası");
      } else if (isOffline) {
        setSyncState("pending");
        let msg = "Cihazda bekliyor — sunucuya aktarılmadı. Bağlantı gelince otomatik denenecek.";
        if (pendingPhotos.length > 0) {
          msg += ` ⚠️ ${pendingPhotos.length} fotoğraf çevrimdışı kuyruklanamaz — bağlantı geldikten sonra ekleyin.`;
        }
        setSyncMessage(msg);
        toast.warning(pendingPhotos.length > 0
          ? "Cihazda bekliyor. Fotoğraflar için bağlantı gerekli."
          : "Cihazda bekliyor: bağlantı gelince gönderilecek",
          { duration: 8000 });
      } else {
        setSyncState("failed");
        setSyncMessage(`Senkronizasyon başarısız (${status || "hata"}) — tekrar deneyebilirsiniz`);
        toast.error("Kayıt başarısız — Cihazda bekliyor olarak saklandı");
      }
      console.error("Quick save failed:", e?.message || e);
    } finally { setSaving(false); }
  };

  const retryPending = async () => {
    const list = getPendingQuickEntries();
    if (list.length === 0) return;
    setSyncState("saving");
    setSyncMessage(`${list.length} bekleyen kayıt gönderiliyor…`);
    let ok = 0, fail = 0;
    for (const item of list) {
      try {
        await axios.post(`${API_URL}/api/properties`, item.payload, {
          withCredentials: true,
          headers: { "x-idempotency-key": item.clientKey },
        });
        removePendingQuickEntry(item.clientKey);
        ok++;
      } catch (e) { fail++; }
    }
    setPendingCount(getPendingQuickEntries().length);
    if (fail === 0) {
      setSyncState("saved");
      setSyncMessage(`${ok} kayıt sunucuya aktarıldı ✓`);
      toast.success(`${ok} bekleyen kayıt sunucuya aktarıldı`);
    } else {
      setSyncState("failed");
      setSyncMessage(`${ok} başarılı, ${fail} hala başarısız`);
      toast.error(`${fail} kayıt hala başarısız`);
    }
    onSaved?.();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="quick-field-entry"
        // Override shadcn Dialog's sm:grid / sm:h-auto / sm:gap-4 defaults.
        // Force flex column at every breakpoint so the header + scrollable
        // body + sticky footer layout actually works.
        className="w-full h-full sm:!h-auto sm:max-w-md sm:!max-h-[calc(100dvh-24px)] bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A] overflow-hidden !flex !flex-col sm:!grid-cols-none sm:!gap-0 !p-0"
        style={{ maxHeight: "calc(100dvh - 24px)" }}
      >
        <DialogHeader className="p-4 pb-2 border-b border-[#E4E4E7] dark:border-[#27272A] flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#D97706] flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-[#0A0A0A] dark:text-[#FAFAFA]">
                Hızlı Saha Kaydı
              </DialogTitle>
              <p className="text-[10px] text-[#52525B] dark:text-[#A1A1AA]">
                Sadece Kısa Başlık zorunlu. Gerisi sonra tamamlanabilir.
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Sync status banner — surfaces pending unsynced entries */}
        {(syncState !== "idle" || pendingCount > 0) && (
          <div
            data-testid="quick-sync-status"
            className={`px-4 py-2 text-xs font-medium border-b flex items-center justify-between gap-2 flex-shrink-0 ${
              syncState === "saved"
                ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
                : syncState === "pending"
                ? "bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800"
                : syncState === "failed"
                ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800"
                : syncState === "saving"
                ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800"
                : "bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800"
            }`}
          >
            <span data-testid="quick-sync-message" className="flex-1 truncate">
              {syncMessage || (pendingCount > 0 ? `${pendingCount} kayıt cihazda bekliyor` : "")}
            </span>
            {pendingCount > 0 && syncState !== "saving" && (
              <button
                data-testid="quick-retry-pending"
                onClick={retryPending}
                className="underline underline-offset-2 font-semibold whitespace-nowrap"
                type="button"
              >
                Tekrar dene ({pendingCount})
              </button>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Kısa başlık */}
          <div>
            <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Kısa Başlık *</Label>
            <Input
              data-testid="quick-title-input"
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="Örn: Moonlight havuzlu 1+1"
              autoFocus
              className="mt-1 h-12 text-base bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
            />
          </div>

          {/* Durum + Fiyat */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Durum (İşlem)</Label>
              <Select value={form.status} onValueChange={(v) => set({ status: v })}>
                <SelectTrigger className="mt-1 h-11 bg-white dark:bg-[#09090B]"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_LIST.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Fiyat</Label>
              <div className="mt-1 flex gap-1">
                <div className="flex-1">
                  <MoneyInput
                    data-testid="quick-price-input"
                    value={form.current_price}
                    onChange={(n) => set({ current_price: n == null ? "" : n })}
                    currency={form.currency}
                    currencyOther={form.currency_other}
                  />
                </div>
                <Select value={form.currency || "TRY"} onValueChange={(v) => set({ currency: v })}>
                  <SelectTrigger data-testid="quick-currency-select" className="w-[88px] h-11 bg-white dark:bg-[#09090B]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>{CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.currency === "Diğer" && (
                <Input
                  data-testid="quick-currency-other-input"
                  value={form.currency_other || ""}
                  onChange={(e) => set({ currency_other: e.target.value.slice(0, 8) })}
                  placeholder="CHF, AED..."
                  className="mt-1 h-9 text-xs bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
                />
              )}
            </div>
          </div>

          {/* GPS */}
          <div>
            <Button
              type="button"
              data-testid="quick-capture-location-button"
              onClick={captureLocation}
              disabled={locating}
              className="w-full h-12 bg-[#F59E0B] hover:bg-[#D97706] text-white"
            >
              <Locate className={`w-5 h-5 mr-2 ${locating ? "animate-pulse" : ""}`} />
              {locating ? "Konum alınıyor..." : form.lat ? `Konum kaydedildi (±${form.location_accuracy}m)` : "Konumumu Kaydet"}
            </Button>
            {form.lat && form.lng ? (
              <div className="mt-2">
                <LocationPreview
                  lat={Number(form.lat)}
                  lng={Number(form.lng)}
                  accuracy={form.location_accuracy}
                  capturedAt={form.location_captured_at}
                  onCorrect={(newLat, newLng) => set({
                    lat: newLat, lng: newLng,
                    location_accuracy: null,
                    location_captured_at: new Date().toISOString(),
                  })}
                />
                {form.location && (
                  <div className="mt-1 text-[10px] text-center text-[#52525B] dark:text-[#A1A1AA] flex items-center justify-center gap-1">
                    <Check className="w-3 h-3 text-emerald-500" />
                    {form.location}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Camera */}
          <div>
            <Button
              type="button"
              data-testid="quick-camera-button"
              onClick={() => cameraRef.current?.click()}
              variant="outline"
              className="w-full h-12 border-[#E4E4E7] dark:border-[#27272A]"
            >
              <Camera className="w-5 h-5 mr-2" />
              Fotoğraf Çek {pendingPhotos.length > 0 && `(${pendingPhotos.length})`}
            </Button>
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={(e) => addPhotos(e.target.files)}
            />
            {pendingPhotos.length > 0 && (
              <p className="text-[10px] mt-1 text-center text-[#52525B] dark:text-[#A1A1AA]">
                Fotoğraflar arka planda yüklenecek
              </p>
            )}
          </div>

          {/* Quick chips */}
          <QuickChips label="Tip" value={form.property_type} options={PROPERTY_TYPES.slice(0, 6)} onChange={(v) => set({ property_type: v })} />
          <QuickChips label="Oda" value={form.room_count} options={ROOM_COUNTS.slice(0, 6)} onChange={(v) => set({ room_count: v })} />

          {/* Bulunduğu Kat — single-choice select. Legacy values that
              don't map to an option are shown as a hint and preserved. */}
          <div>
            <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Bulunduğu Kat</Label>
            <SingleChoiceSelect
              testId="quick-current-floor-select"
              value={floorSelection}
              onChange={setFloorSelection}
              options={FLOOR_OPTIONS}
              placeholder="Kat seçin"
              legacyText={
                floorSelection === "" &&
                form.current_floor != null &&
                String(form.current_floor).trim() !== ""
                  ? String(form.current_floor)
                  : null
              }
            />
          </div>

          {/* Bina Yaşı — single-choice select. 0..50 or "Yapım aşamasında". */}
          <div>
            <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Bina Yaşı</Label>
            <SingleChoiceSelect
              testId="quick-building-age-select"
              value={ageSelection}
              onChange={setAgeSelection}
              options={BUILDING_AGE_OPTIONS}
              placeholder="Bina yaşı seçin"
              legacyText={
                ageSelection === "" &&
                form.building_age != null &&
                String(form.building_age).trim() !== ""
                  ? String(form.building_age)
                  : null
              }
            />
          </div>

          <QuickChips label="Teknik Durum" value={form.technical_condition} options={CONDITION_LIST.slice(0, 5)} onChange={(v) => set({ technical_condition: v })} />

          {/* Feature quick toggles (subset) */}
          <div>
            <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Hızlı Notlar</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {[...FEATURES_POSITIVE.slice(0, 4), ...FEATURES_NEGATIVE.slice(0, 4)].map((f) => {
                const active = form.features.includes(f.key);
                const isNeg = FEATURES_NEGATIVE.some(x => x.key === f.key);
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => toggleFeat(f.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors duration-150 ${
                      active
                        ? isNeg ? "bg-red-500 text-white border-red-500" : "bg-emerald-500 text-white border-emerald-500"
                        : "bg-white dark:bg-[#09090B] text-[#52525B] dark:text-[#A1A1AA] border-[#E4E4E7] dark:border-[#27272A]"
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Kısa not */}
          <div>
            <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Kısa Not</Label>
            <Textarea
              rows={2}
              value={form.note}
              onChange={(e) => set({ note: e.target.value })}
              placeholder="Emlakçı ne dedi, ilginç detay..."
              className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
            />
          </div>
        </div>

        {/* Sticky footer — save controls always reachable, keyboard-safe */}
        <div
          className="border-t border-[#E4E4E7] dark:border-[#27272A] p-3 bg-white dark:bg-[#18181B] flex-shrink-0 grid grid-cols-2 gap-2"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <Button
            type="button"
            data-testid="quick-save-button"
            onClick={() => submit(false)}
            disabled={saving}
            className="h-12 bg-[#0A0A0A] hover:bg-[#18181B] text-white dark:bg-[#FAFAFA] dark:text-[#0A0A0A]"
          >
            <Save className="w-4 h-4 mr-1" />
            Kaydet
          </Button>
          <Button
            type="button"
            data-testid="quick-save-new-button"
            onClick={() => submit(true)}
            disabled={saving}
            className="h-12 bg-[#F59E0B] hover:bg-[#D97706] text-white"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Kaydet & Yeni
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const QuickChips = ({ label, value, options, onChange }) => (
  <div>
    <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">{label}</Label>
    <div className="mt-1 flex flex-wrap gap-1.5">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(value === opt ? "" : opt)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors duration-150 ${
            value === opt
              ? "bg-[#0A0A0A] text-white border-[#0A0A0A] dark:bg-[#FAFAFA] dark:text-[#0A0A0A] dark:border-[#FAFAFA]"
              : "bg-white dark:bg-[#09090B] text-[#52525B] dark:text-[#A1A1AA] border-[#E4E4E7] dark:border-[#27272A]"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

export default QuickFieldEntry;
